import { initAligningGuidelines } from "fabric/extensions";
import { type MutableRefObject, useCallback, useEffect, useRef } from "react";
import { BaseSizedContainer } from "../../../../../components/Content/BaseSizedContainer.tsx";
import { ZoomableComponent } from "../../../../../components/Zoom/ZoomableComponent.tsx";
import { useDebouncedFunction } from "../../../../../hooks/useDebouncedFunction.ts";
import { useFitZoom } from "../../../../../hooks/useFitZoom.ts";
import {
  DEFAULT_IMAGE_SIZE,
  UPLOAD_MAX_IMAGE_SIZE,
} from "../../../../constants.ts";
import { LoadingTile } from "../../../components/GeneratedContent/LoadingTile.tsx";
import { useBoardUuid } from "../../../hooks/useBoardUuid.ts";
import { useCompileEditionCanvas } from "../../../hooks/useCompileEditionCanvas.ts";
import { useComposition } from "../../../hooks/useComposition.ts";
import { useUpdateComposition } from "../../../hooks/useUpdateComposition.ts";
import { useUploadImage } from "../../../hooks/useUploadImage.ts";
import type { DetailedAsset } from "../../../types.ts";
import type { BaseCanvasStore } from "../components/FabricCanvas/canvasStoreFactory.ts";
import { FabricCanvas } from "../components/FabricCanvas/FabricCanvas.tsx";
import { AssetLayerBuilder } from "./AssetLayerBuilder.tsx";
import {
  ASSET_LAYER_TYPE,
  DEFAULT_BACKGROUND_COLOR,
  DESIGNER_CANVAS_ID,
  IMAGE_LAYER_TYPE,
} from "./constants.ts";
import { useCanvasAutoPersistence } from "./hooks/useCanvasAutoPersistence.ts";
import { useCanvasCopyPasteKeyboardShortcuts } from "./hooks/useCanvasCopyPasteKeyboardShortcuts.ts";
import { useCanvasDeleteKeyboardShortcut } from "./hooks/useCanvasDeleteKeyboardShortcut.ts";
import { useCanvasEventsListener } from "./hooks/useCanvasEventsListener.ts";
import { useCanvasHistoryKeyboardShortcut } from "./hooks/useCanvasHistoryKeyboardShortcut.ts";
import { useCreateAssetComposition } from "./hooks/useCreateAssetComposition.ts";
import { useCreateLayer } from "./hooks/useCreateLayer.ts";
import { useLayers } from "./hooks/useLayers.ts";
import { useSelectedLayerType } from "./hooks/useSelectedLayerType.ts";
import { useSelectLayers } from "./hooks/useSelectLayers.ts";
import { ImageLayerBuilder } from "./ImageLayerBuilder.tsx";
import { designerCanvasStore } from "./stores/designerCanvasStore.ts";
import { fabricObjectToLayer } from "./typeguard.ts";
import type { Layer } from "./types.ts";
import { deleteSelectedLayers } from "./utils/layer.ts";

export const DesignerCanvas = ({
  asset,
  areLayersLocked = true,
  overlayCanvasStore,
}: {
  asset: DetailedAsset;
  areLayersLocked?: boolean;
  overlayCanvasStore?: BaseCanvasStore;
}) => {
  const {
    fabricCanvas,
    isDrawingMode,
    isDeletionDisabled,
    canvasSize,
    backgroundColor,
  } = designerCanvasStore.useState();

  const createLayer = useCreateLayer();

  const { selectLayers } = useSelectLayers();

  const selectLayersIfNotLocked = useCallback(
    (layers: Layer[], active = true) => {
      if (areLayersLocked) return;
      selectLayers(layers, active);
    },
    [areLayersLocked, selectLayers],
  );

  const layers = useLayers();

  const isAssetLayer = layers.some(
    (layer) => layer.layerType === ASSET_LAYER_TYPE,
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const defaultedCanvasSize =
    canvasSize ??
    (asset.content.width && asset.content.height
      ? {
          width: asset.content.width,
          height: asset.content.height,
        }
      : undefined);

  const { createAssetComposition } = useCreateAssetComposition();
  const { updateComposition } = useUpdateComposition();

  const updateCompositionCanvas = useDebouncedFunction(
    useCallback(() => {
      if (!asset.composition_uuid) {
        createAssetComposition(asset.uuid);
      } else {
        const serializedCanvas = designerCanvasStore.serialize();
        updateComposition({
          uuid: asset.composition_uuid,
          fabricCanvasData: serializedCanvas,
        });
      }
    }, [
      asset.uuid,
      asset.composition_uuid,
      createAssetComposition,
      updateComposition,
    ]),
    1000,
  );

  const { mutate: uploadRawPng } = useUploadImage({
    maxSize: UPLOAD_MAX_IMAGE_SIZE,
    pipelineCompatible: false,
  });
  const compileEditionCanvas = useCompileEditionCanvas();
  const boardUuid = useBoardUuid();
  const { updateComposition: updateCompositionAndInvalidateAssets } =
    useUpdateComposition([`boards/${boardUuid}/assets`]);
  const updateCompositionThumb = useDebouncedFunction(
    useCallback(async () => {
      try {
        if (
          compileEditionCanvas &&
          asset.composition_uuid &&
          asset.content.url &&
          asset.content.resource_type === "IMAGE"
        ) {
          const blob = await compileEditionCanvas(
            asset.content.url,
            asset.content.resource_type,
            "jpeg",
            0.5,
          );

          const thumbnailImageUuid = await new Promise<string>((resolve) => {
            uploadRawPng({
              image: blob,
              onMutateSuccess: (uuid) => resolve(uuid),
            });
          });

          updateCompositionAndInvalidateAssets({
            uuid: asset.composition_uuid,
            thumbnailImageUuid,
          });
        }
      } catch (error) {
        console.warn("Failed to compile or upload canvas thumbnail", error);
      }
    }, [
      asset.composition_uuid,
      asset.content.resource_type,
      asset.content.url,
      compileEditionCanvas,
      updateCompositionAndInvalidateAssets,
      uploadRawPng,
    ]),
    10000,
  );

  const { composition } = useComposition(asset.composition_uuid);

  const loadFromStorage = useCallback(async () => {
    if (composition?.fabric_canvas_data) {
      if (!asset.content.width || !asset.content.height) return;
      await designerCanvasStore.deserialize({
        saved: composition.fabric_canvas_data,
        defaultCanvasSize: {
          width: asset.content.width,
          height: asset.content.height,
        },
      });
    }
  }, [
    composition?.fabric_canvas_data,
    asset.content.width,
    asset.content.height,
  ]);

  const isAligningGuidelinesEnabled = useRef(false);
  useEffect(() => {
    if (fabricCanvas && !isAligningGuidelinesEnabled.current) {
      initAligningGuidelines(fabricCanvas);
      isAligningGuidelinesEnabled.current = true;
    }
  }, [fabricCanvas]);

  useEffect(() => {
    if (!isAssetLayer && asset.uuid && canvasSize !== undefined) {
      createLayer({ layerType: ASSET_LAYER_TYPE, assetUuid: asset.uuid });
    }
  }, [isAssetLayer, createLayer, asset.uuid, canvasSize]);

  useEffect(() => {
    if (
      asset.content.width &&
      asset.content.height &&
      canvasSize === undefined
    ) {
      designerCanvasStore.setCanvasSize({
        width: asset.content.width,
        height: asset.content.height,
      });
    }
  }, [asset.content.width, asset.content.height, canvasSize]);

  useEffect(() => {
    if (backgroundColor === undefined) {
      designerCanvasStore.setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
    }
  }, [backgroundColor]);

  const paste = useCallback(() => {
    const newLayers = designerCanvasStore.paste();
    if (newLayers) selectLayersIfNotLocked(newLayers);
  }, [selectLayersIfNotLocked]);

  useCanvasAutoPersistence({
    fabricCanvas,
    onCanvasInit: loadFromStorage,
    onCanvasChange: () => {
      updateCompositionCanvas();
      updateCompositionThumb();
    },
  });

  useCanvasCopyPasteKeyboardShortcuts({
    fabricCanvas,
    copy: designerCanvasStore.copy,
    paste,
  });

  useEffect(() => {
    designerCanvasStore.clearHistory();
  }, [asset.uuid]);

  // XXX: We use asset uuid as history snapshot id to avoid RACE issues when switching between assets and avoid mixing up history snapshots.
  const saveHistorySnapshot = useCallback(() => {
    designerCanvasStore.saveHistorySnapshot(asset.uuid);
  }, [asset.uuid]);

  const undo = useCallback(async () => {
    await designerCanvasStore.undo(asset.uuid);
  }, [asset.uuid]);

  const redo = useCallback(async () => {
    await designerCanvasStore.redo(asset.uuid);
  }, [asset.uuid]);

  useCanvasHistoryKeyboardShortcut({
    fabricCanvas,
    saveHistorySnapshot,
    undo,
    redo,
  });

  useCanvasDeleteKeyboardShortcut({
    fabricCanvas,
    isDeletionDisabled,
    onDelete: () => {
      if (fabricCanvas) deleteSelectedLayers(fabricCanvas);
    },
  });

  useCanvasVideoPlayback(videoRef);

  useLockLayers(areLayersLocked);

  useSelectLayerOnCanvasSelection(areLayersLocked);

  useRemoveLayerOnCanvasRemoval();

  useCanvasTextEditing();

  useAutomaticZoomFit();

  // Reset the canvas dimensions when the component is unmounted to avoid having a stale dimension in the store when the component is remounted with a different content.
  useEffect(
    () => () => {
      designerCanvasStore.setCanvasSize(undefined);
    },
    [],
  );

  // Make sure to clean the fabric canvas in store when the component is unmounted.
  // FIXME(@tomasyany): Ideally, we would clear the fabric canvas in a lower component (e.g.
  // FabricCanvas one) so that the store is always up-to-date with the reality.
  // But for some reason there is an infinite rendering when lowering the responsability,
  // and I couldn't find an easy workaround, so I'm putting it here.
  useEffect(
    () => () => {
      designerCanvasStore.setFabricCanvas(undefined);
      overlayCanvasStore?.setFabricCanvas(undefined);
    },
    [overlayCanvasStore],
  );

  if (asset.content.status === "PROCESSING" || asset.content.url === null) {
    return (
      <BaseSizedContainer
        width={canvasSize?.width ?? DEFAULT_IMAGE_SIZE}
        height={canvasSize?.height ?? DEFAULT_IMAGE_SIZE}
        className="p-1200"
      >
        <LoadingTile content={asset.content} className="h-full w-full" />
      </BaseSizedContainer>
    );
  }

  return (
    <>
      {defaultedCanvasSize && (
        // FIXME:  Instead of using `ZoomableComponent` that relies on CSS transforms, we could use `fabric.js`'s `zoomToPoint` method.
        // Current approach: CSS `transform: scale()` maintains original canvas dimensions while visually scaling content.
        // This preserves coordinate consistency across the app but can cause rendering quality degradation at high zoom levels (>5x).
        // Alternative approach: `fabric.js`'s `zoomToPoint` would provide better rendering quality but requires significant refactoring:
        // 1. Aligning guidelines (`initAligningGuidelines`) would need recalibration when canvas dimensions change
        // 2. Scroll/overflow handling in `ZoomableComponent` assumes fixed canvas size for centering and bounds calculations
        // 3. Layer serialization/positioning logic depends on stable `canvasSize` reference throughout the app
        // 4. Coordinate system transformations between canvas space and screen space would need updates
        // The CSS transform approach is currently more stable and avoids coordinate system complexity,
        // but should be reconsidered if high-fidelity zoom rendering becomes a priority.
        <ZoomableComponent
          zoomableComponentId={DESIGNER_CANVAS_ID}
          zoomableComponentRealSize={defaultedCanvasSize}
          children={
            <div className="relative">
              <div className="absolute inset-0">
                <FabricCanvas
                  initWidth={defaultedCanvasSize.width}
                  initHeight={defaultedCanvasSize.height}
                  onCanvasReady={(canvas) => {
                    // XXX: To avoid rerendering the canvas when the canvas is resized, we only update the canvas if it is different from the current one.
                    if (
                      designerCanvasStore.getState().fabricCanvas !== canvas
                    ) {
                      designerCanvasStore.setFabricCanvas(canvas);
                    }
                  }}
                  isDrawingMode={isDrawingMode}
                />
              </div>
              {overlayCanvasStore && (
                <div className="absolute inset-0">
                  <FabricCanvas
                    initWidth={defaultedCanvasSize.width}
                    initHeight={defaultedCanvasSize.height}
                    onCanvasReady={(canvas) => {
                      // XXX: To avoid rerendering the canvas when the canvas is resized, we only update the canvas if it is different from the current one.
                      if (
                        overlayCanvasStore.getState().fabricCanvas !== canvas
                      ) {
                        overlayCanvasStore.setFabricCanvas(canvas);
                      }
                    }}
                    isDrawingMode={overlayCanvasStore.getState().isDrawingMode}
                  />
                </div>
              )}
            </div>
          }
        />
      )}
      {/*
        XXX: Render `ImageLayerBuilder` components for each image layer to construct Fabric.js objects.
        These components handle the logic of creating and updating fabric objects without rendering DOM elements.
      */}
      {layers
        .filter((layer) => layer.layerType === IMAGE_LAYER_TYPE)
        .map((layer) => (
          <ImageLayerBuilder
            key={layer.id}
            layer={layer}
            fabricCanvas={fabricCanvas}
          />
        ))}
      {/*
        XXX: Render `AssetLayerBuilder` components for each asset layer to construct Fabric.js objects.
        These components handle the logic of creating and updating fabric objects without rendering DOM elements.
      */}
      {layers
        .filter((layer) => layer.layerType === ASSET_LAYER_TYPE)
        .map((layer) => (
          <AssetLayerBuilder
            key={layer.id}
            layer={layer}
            videoRef={videoRef}
            fabricCanvas={fabricCanvas}
          />
        ))}
    </>
  );
};

const useLockLayers = (areLayersLocked: boolean) => {
  const { fabricCanvas } = designerCanvasStore.useState();

  useEffect(() => {
    if (fabricCanvas) {
      for (const obj of fabricCanvas.getObjects()) {
        obj.set({
          selectable: !areLayersLocked,
          evented: !areLayersLocked,
        });
      }
      fabricCanvas.renderAll();
    }
  }, [fabricCanvas, areLayersLocked]);
};

const useSelectLayerOnCanvasSelection = (areLayersLocked: boolean) => {
  const { fabricCanvas } = designerCanvasStore.useState();
  const { selectLayers } = useSelectLayers();
  const selectLayersIfNotLocked = useCallback(
    (layers: Layer[], active = true) => {
      if (areLayersLocked) return;
      selectLayers(layers, active);
    },
    [areLayersLocked, selectLayers],
  );
  const selectedLayerType = useSelectedLayerType();

  useCanvasEventsListener({
    eventTypes: ["selection:created", "selection:updated", "selection:cleared"],
    onEvent: () => {
      if (!fabricCanvas) return;
      const activeObjects = fabricCanvas.getActiveObjects();
      if (
        activeObjects.length === 0 &&
        selectedLayerType === ASSET_LAYER_TYPE
      ) {
        return;
      }
      selectLayersIfNotLocked(
        activeObjects
          .map((obj) => designerCanvasStore.getLayerByFabricObject(obj))
          .filter((layer) => layer !== undefined),
        // XXX: We don't want to activate the layers when the selection is updated, to avoid circular calls with canvas manual selection.
        false,
      );
    },
    canvas: fabricCanvas,
  });
};

const useCanvasTextEditing = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  useCanvasEventsListener({
    eventTypes: ["text:editing:entered"],
    onEvent: () => {
      designerCanvasStore.setIsDeletionDisabled(true);
      designerCanvasStore.setIsPasteDisabled(true);
      designerCanvasStore.setIsTextEditing(true);
    },
    canvas: fabricCanvas,
  });

  useCanvasEventsListener({
    eventTypes: ["text:editing:exited"],
    onEvent: () => {
      designerCanvasStore.setIsDeletionDisabled(false);
      designerCanvasStore.setIsPasteDisabled(false);
      designerCanvasStore.setIsTextEditing(false);
    },
    canvas: fabricCanvas,
  });
};

const useRemoveLayerOnCanvasRemoval = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  useCanvasEventsListener({
    eventTypes: ["object:removed"],
    onEvent: (e) => {
      // XXX: The object:removed event fires after the object has already been removed from the canvas.
      // At this point, canvas methods like getLayers() can't access the removed object, so we can't
      // retrieve the layer by ID. Instead, we extract the layer data directly from the event target
      // using `toObject()` to get the serialized fabric object data before it's completely gone, and
      // inject directly into our `removeLayer` function.
      const layer = fabricObjectToLayer(e.target.toObject());
      if (layer) designerCanvasStore.removeLayer(layer);
      fabricCanvas?.renderAll();
    },
    canvas: fabricCanvas,
  });
};

const useCanvasVideoPlayback = (
  videoRef: MutableRefObject<HTMLVideoElement | null>,
) => {
  const { fabricCanvas, isVideoPlaying } = designerCanvasStore.useState();
  useEffect(() => {
    const renderLoop = () => {
      if (!videoRef.current?.paused && !videoRef.current?.ended) {
        fabricCanvas?.renderAll();
        requestAnimationFrame(renderLoop);
      }
    };

    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current
          .play()
          .then(() => {
            renderLoop();
          })
          .catch((error) => {
            console.error("Error playing video:", error);
          });
      } else {
        videoRef.current.pause();
      }
    }
  }, [fabricCanvas, isVideoPlaying, videoRef]);
};

const useAutomaticZoomFit = () => {
  const { canvasSize } = designerCanvasStore.useState();
  const fitZoom = useFitZoom({
    onFitZoomComputed: designerCanvasStore.setZoomLevel,
    zoomableComponentId: DESIGNER_CANVAS_ID,
    zoomableComponentRealSize: canvasSize ?? { width: 0, height: 0 },
  });

  useEffect(() => {
    if (canvasSize) fitZoom();
  }, [canvasSize, fitZoom]);
};
